import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useMonths } from "../hooks/useMonths";
import { useReportingCycleContext } from "../contexts/reportingCycleContext";
import { ReportingCycleEnum } from "../api/models/reportingCycle.models";
import { Report } from "../api/models/report.models";

interface ChooseYearDialogProps {
  show: boolean;
  onHide: () => void;
  onSelect: (year: number, quarter: number, month: number | null) => void;
  existingReports?: Report[];
}

interface QuarterOption {
  label: string;
  value: number;
}

const Row = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  margin-bottom: 1rem;

  .field {
    flex: 1;
    display: flex;
    flex-direction: column;

    label {
      margin-bottom: 0.5rem;
    }
  }
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 3rem;
`;

const ChooseYearDialog: React.FC<ChooseYearDialogProps> = ({
  show,
  onHide,
  onSelect,
  existingReports,
}) => {
  const { t } = useTranslation();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedQuarter, setSelectedQuarter] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedCycleType, setSelectedCycleType] =
    useState<ReportingCycleEnum>(ReportingCycleEnum.YEARLY);
  const [yearOptions, setYearOptions] = useState<number[]>([]);

  const { reportingCycleData } = useReportingCycleContext();

  const quarterOptions: QuarterOption[] = [
    { label: "Q1", value: 1 },
    { label: "Q2", value: 2 },
    { label: "Q3", value: 3 },
    { label: "Q4", value: 4 },
  ];

  const { monthOptions } = useMonths();

  useEffect(() => {
    if (reportingCycleData) {
      //const years = reportingCycleData.map((cycle) => cycle.year); Estetään raportointi muille kuin vuodelle 2025
      const years = [2025];
      setYearOptions([...new Set(years)].sort((a, b) => b - a));
    }
  }, [reportingCycleData]);

  useEffect(() => {
    if (reportingCycleData && selectedYear !== null) {
      const cycle = reportingCycleData.find(
        (cycle) => cycle.year === selectedYear
      );
      if (cycle) {
        setSelectedCycleType(cycle.cycle);
      }
    }
  }, [reportingCycleData, selectedYear]);

  const handleSubmit = () => {
    switch (selectedCycleType) {
      case ReportingCycleEnum.YEARLY:
        onSelect(selectedYear as number, 0, null);
        break;
      case ReportingCycleEnum.QUARTERLY:
        onSelect(selectedYear as number, selectedQuarter as number, null);
        break;
      case ReportingCycleEnum.MONTHLY:
        onSelect(selectedYear as number, 0, selectedMonth as number);
        break;
      default:
        break;
    }
    onHide();
  };

  const getQuarterOptions = () => {
    if (!existingReports || selectedYear === null) return quarterOptions;

    const reportsForYear = existingReports.filter(
      (report) => report.year === selectedYear
    );

    const usedQuarters = reportsForYear
      .filter((report) => report.quarter)
      .map((report) => report.quarter);

    return quarterOptions.filter(
      (option) => !usedQuarters.includes(option.value)
    );
  };

  const getMonthOptions = () => {
    if (!existingReports || selectedYear === null) return monthOptions;

    const reportsForYear = existingReports.filter(
      (report) => report.year === selectedYear
    );
    const usedMonths = reportsForYear
      .filter((report) => report.month)
      .map((report) => report.month);

    return monthOptions.filter((option) => !usedMonths.includes(option.value));
  };

  const getYearOptions = () => {
    if (!existingReports || selectedCycleType !== ReportingCycleEnum.YEARLY)
      return yearOptions;

    const usedYears = existingReports.map((report) => report.year);
    return yearOptions.filter((year) => !usedYears.includes(year));
  };

  return (
    <Dialog
      header={t("header-choose-reporting-cycle")}
      visible={show}
      onHide={onHide}
      style={{ width: "500px" }}
    >
      <Row>
        <div className="field">
          <label htmlFor="year">{t("label-year")}</label>
          <Dropdown
            id="year"
            value={selectedYear}
            options={getYearOptions()}
            onChange={(e) => setSelectedYear(e.value)}
            placeholder={t("label-year")}
          />
        </div>
        <div className="field">
          <label htmlFor="quarter">{t("label-quarter")}</label>
          <Dropdown
            id="quarter"
            value={selectedQuarter}
            options={getQuarterOptions()}
            onChange={(e) => setSelectedQuarter(e.value)}
            optionLabel="label"
            placeholder={t("label-quarter")}
            disabled={
              selectedCycleType !== ReportingCycleEnum.QUARTERLY ||
              !selectedYear
            }
          />
        </div>
        <div className="field">
          <label htmlFor="month">{t("label-month")}</label>
          <Dropdown
            id="month"
            value={selectedMonth}
            options={getMonthOptions()}
            onChange={(e) => setSelectedMonth(e.value)}
            optionLabel="label"
            placeholder={t("label-month")}
            disabled={
              selectedCycleType !== ReportingCycleEnum.MONTHLY || !selectedYear
            }
          />
        </div>
      </Row>

      <DialogActions>
        <Button
          label={t("cmd-cancel")}
          onClick={onHide}
          className="p-button-text"
          outlined
        />
        <Button
          label={t("cmd-proceed")}
          icon="pi pi-check"
          onClick={handleSubmit}
          disabled={!selectedYear && !selectedQuarter && !selectedMonth}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ChooseYearDialog;
